import { toInteger } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DrawSvg } from '@/utils/draw-svg-kit';
import {
    EmptyPointImage,
    fetchVectorText,
    getVectorTextFromCache,
    parseColorToRgba,
    parseStrokeDasharray,
} from '@/utils/file-kit';
import { getLayerImageStyle, getLayerInnerStyle, getLayerWrapStyle } from '@/utils/layer-kit';
import { renderPointStyle } from '@/utils/render-kit';
import { BrushPointType, BrushType, IBrushLayerProps, IRenderLayerEvent, IRenderLayerProps } from '@/version-types';
import {
    BRUSH_POINT_SIZE,
    createBrushImage,
    drawBrushStroke,
    exportBrushPath,
    generateLinePath,
    getArrowPointImage,
    isArrowSvg,
    isCodeSvg,
    isCurveSvg,
    isWavySvg,
    LAYER_EDGE_GAP,
    matchRepeatPath,
} from '../utils';

const BrushImage: React.FC<IRenderLayerProps & { normalIcon?: boolean } & IRenderLayerEvent> = ({
    layer,
    // 是否为设计师编辑器图层icon
    normalIcon = false,
    isNavigator = false,
    renderEnd,
    enterLoading,
    exitLoading,
}) => {
    const { opacity, width, height, id } = layer;

    const {
        url,
        __drawPath,
        __isDrawPreview,
        brushType,
        fills,
        stroke,
        startPoint,
        endPoint,
        radius,
        naturalWidth,
        naturalHeight,
        imageTransform: {
            widthRatio,
            heightRatio,
            flipH,
            flipV,
            offsetX: offsetXRatio,
            offsetY: offsetYRatio,
            scale,
            rotate,
        },
    } = layer.data as IBrushLayerProps;

    const originImgWidth = toInteger(width * widthRatio);
    const originImgHeight = toInteger(height * heightRatio);
    const offsetX = toInteger(offsetXRatio * originImgWidth * scale);
    const offsetY = toInteger(offsetYRatio * originImgHeight * scale);
    const isDraw = isCodeSvg(brushType);
    const isCurve = isCurveSvg(brushType);
    const isArrow = isArrowSvg(brushType);
    const isWavy = isWavySvg(brushType);

    const strokeCanvas = useRef<HTMLCanvasElement>(null);
    const brushPathDText = useRef<string>('');

    const [_, setRefreshRender] = useState<number>(0);

    const wrapStyle = getLayerWrapStyle(opacity);
    const innerStyle = getLayerInnerStyle(originImgWidth, originImgHeight, offsetX, offsetY, scale, flipH, flipV);

    // # begin 绘制箭头端点
    const startPointText = getVectorTextFromCache(startPoint.url);
    const hasStartPointText = !!startPointText;
    const endPointText = getVectorTextFromCache(endPoint.url);
    const hasEndPointText = !!endPointText;

    const startPointImg = useMemo(() => {
        if (!isArrow || !hasStartPointText) return EmptyPointImage;
        const text = getVectorTextFromCache(startPoint.url);
        return getArrowPointImage(text, startPoint, fills[0]);
    }, [isArrow, hasStartPointText, startPoint.url, fills[0].width, fills[0].color]);

    const endPointImg = useMemo(() => {
        if (!isArrow || !hasEndPointText) return EmptyPointImage;
        const text = getVectorTextFromCache(endPoint.url);
        return getArrowPointImage(text, endPoint, fills[0]);
    }, [isArrow, hasEndPointText, endPoint.url, fills[0].width, fills[0].color]);
    // # end 绘制箭头端点

    // # begin 绘制曲线笔, 波浪笔, 直线笔
    const brushSvgText = getVectorTextFromCache(url);
    const hasBrushSvgText = !!brushSvgText;
    const brushImage = useMemo(() => {
        if (isDraw || isArrow) return EmptyPointImage;
        const createImage = (pathD: string) => {
            brushPathDText.current = pathD;
            if (isWavy) {
                const res = { ...EmptyPointImage, src: '' };
                res.src = matchRepeatPath(pathD);
                return res;
            }
            return createBrushImage(pathD, {
                brushType,
                fills,
                stroke,
                naturalWidth,
                naturalHeight,
                __isDrawPreview,
                width,
                height,
            });
        };
        if (__drawPath) {
            return createImage(__drawPath);
        } else {
            if (url) {
                // 曲线笔, 波浪笔
                if (!hasBrushSvgText) return EmptyPointImage;
                const text = getVectorTextFromCache(url);
                const pathD = exportBrushPath(text);
                if (pathD) {
                    return createImage(pathD);
                }
                return EmptyPointImage;
            } else {
                // 直线笔
                const pathD = generateLinePath({ x: 0, y: 0 }, { x: naturalWidth, y: 0 });
                return createImage(pathD);
            }
        }
    }, [
        isDraw,
        isArrow,
        isWavy,
        __drawPath,
        __isDrawPreview,
        hasBrushSvgText,
        url,
        brushType,
        fills,
        stroke,
        naturalWidth,
        naturalHeight,
        width,
        height,
    ]);

    // # begin 加载远程资源
    useEffect(() => {
        const fetchList: any[] = [];
        if (startPoint.url && !hasStartPointText) {
            fetchList.push(fetchVectorText(startPoint.url));
        }
        if (endPoint.url && !hasEndPointText) {
            fetchList.push(fetchVectorText(endPoint.url));
        }
        if (url && !hasBrushSvgText) {
            fetchList.push(fetchVectorText(url));
        }
        if (fetchList.length) {
            Promise.all(fetchList).then(() => {
                setRefreshRender(new Date().getTime());
            });
        }
    }, [startPoint.url, hasStartPointText, endPoint.url, hasEndPointText, url, hasBrushSvgText]);
    // # end 加载远程资源

    useEffect(() => {
        // 绘制线条笔描边
        if (!isCurve) return;
        if (brushPathDText.current && stroke.width && strokeCanvas.current)
            drawBrushStroke(
                __isDrawPreview || false,
                strokeCanvas.current as HTMLCanvasElement,
                brushPathDText.current,
                {
                    fills,
                    stroke,
                    width,
                    height,
                    naturalWidth,
                    naturalHeight,
                } as any,
            );
    }, [hasBrushSvgText, __isDrawPreview, isCurve, fills, stroke, width, height, naturalWidth, naturalHeight]);

    useEffect(() => {
        if (renderEnd) {
            setTimeout(() => {
                renderEnd();
            }, 0);
        }
    }, [renderEnd]);

    // useEffect(() => {
    //     enterLoading?.(id);
    //     setTimeout(() => {
    //         exitLoading?.(id);
    //     }, 1000);
    // }, [id]);

    // 直线笔锚点
    function getLineAnchorHtml() {
        let anchorHtml = '';
        if (!normalIcon && !isNavigator && brushType === BrushType.Line) {
            anchorHtml = `<div id="brushLineLeft${id}" style="left:${
                stroke.width + LAYER_EDGE_GAP
            }px;top:50%;position:absolute;"></div><div id="brushLineRight${id}" style="right:${
                stroke.width + LAYER_EDGE_GAP
            }px;top:50%;position:absolute;"></div>`;
        }
        return anchorHtml;
    }

    let startPointFixOffset = 0;
    let endPointFixOffset = 0;
    const arrowSvgColor = { rgb: '', a: 1 };
    if (isArrow) {
        const rgba = parseColorToRgba(fills[0].color as string);
        arrowSvgColor.rgb = `rgb(${rgba.r},${rgba.g},${rgba.b})`;
        arrowSvgColor.a = rgba.a;

        const fix = (20 / 100) * fills[0].width;
        if (startPoint.type === BrushPointType.StraightArrow) {
            startPointFixOffset = fix;
        }
        if (endPoint.type === BrushPointType.StraightArrow) {
            endPointFixOffset = fix;
        }
    }

    return (
        <div style={wrapStyle}>
            <div style={innerStyle}>
                {isDraw ? (
                    <DrawSvg
                        showDrawId={!normalIcon}
                        id={layer.id}
                        type={brushType === BrushType.Rect ? 'rect' : 'ellipse'}
                        radius={radius}
                        width={width}
                        height={height}
                        fill={fills[0].color}
                        stroke={stroke.color}
                        strokeWidth={stroke.width}
                        strokeDasharray={stroke.dasharray || ''}
                        strokeLinecap={stroke.linecap || ''}
                    />
                ) : isArrow ? (
                    <div
                        {...(!normalIcon && { 'data-draw-id': `svg-${layer.id}` })}
                        style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                        }}
                    >
                        <svg
                            viewBox={`0 0 ${width} ${fills[0].width}`}
                            xmlns="http://www.w3.org/2000/svg"
                            version="1.1"
                            style={{
                                display: 'block',
                                overflow: 'visible',
                                opacity: arrowSvgColor.a,
                            }}
                            stroke={arrowSvgColor.rgb}
                            fill={arrowSvgColor.rgb}
                        >
                            {renderPointStyle(startPointImg, {
                                transform: `translate(${startPointFixOffset}px,-${
                                    (startPointImg.height - fills[0].width) / 2
                                }px)`,
                            })}
                            <line
                                x1={(startPoint.offset / BRUSH_POINT_SIZE) * fills[0].width + startPointFixOffset}
                                y1={fills[0].width / 2}
                                x2={width - (endPoint.offset / BRUSH_POINT_SIZE) * fills[0].width - endPointFixOffset}
                                y2={fills[0].width / 2}
                                strokeWidth={fills[0].width}
                                strokeDasharray={parseStrokeDasharray(fills[0].dasharray, fills[0].width) as string}
                                strokeLinecap={fills[0].linecap}
                            />
                            {renderPointStyle(endPointImg, {
                                transform: `translate(${width - endPointFixOffset}px,-${
                                    (endPointImg.height - fills[0].width) / 2
                                }px) rotateY(180deg)`,
                            })}
                        </svg>
                    </div>
                ) : isWavy ? (
                    <>
                        {brushImage.src ? (
                            <>
                                <DrawSvg
                                    showDrawId={!normalIcon}
                                    id={layer.id}
                                    type="wavy"
                                    radius={radius}
                                    width={width}
                                    height={height}
                                    fill="none"
                                    stroke={fills[0].color}
                                    strokeWidth={fills[0].width}
                                    strokeDasharray={fills[0].dasharray || ''}
                                    strokeLinecap={fills[0].linecap || ''}
                                    naturalWidth={naturalWidth}
                                    repeatD={brushImage.src}
                                    pathD={brushPathDText.current}
                                />
                            </>
                        ) : null}
                    </>
                ) : (
                    <>
                        {isCurve && stroke?.width ? (
                            // 绘制描边
                            <canvas
                                ref={strokeCanvas}
                                style={{
                                    ...getLayerImageStyle(originImgWidth, originImgHeight),
                                    pointerEvents: 'none',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    zIndex: 1,
                                }}
                                data-stroke-id={`svg-stroke-${id}`}
                            ></canvas>
                        ) : null}
                        {brushImage.src ? (
                            <div
                                {...(!normalIcon && { 'data-draw-id': `svg-${layer.id}` })}
                                style={{
                                    ...getLayerImageStyle(brushImage.width, brushImage.height),
                                    transform: `scale(${originImgWidth / brushImage.width},${
                                        originImgHeight / brushImage.height
                                    })`,
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: `${getLineAnchorHtml()}${brushImage.html}`,
                                }}
                            ></div>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
};
export default React.memo(BrushImage);
